import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { DatePickerComponent, DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { ClientDTO, PagedResultDTOOfUserInfoDTO, PagedResultDTOOfZoneDTO } from '../../../../../base/models/models.swagger.generated';
import { ClientService } from '../../../../../base/services/client/client.service';
import { Observable } from 'rxjs';
import { IAutocompleteItem } from '../../../../../base/shared/autocomplete/autocomplete-item';
import { map } from 'rxjs/operators';
import { FalseRecordingReportService } from '../false-recording-report.service';
import { TranslateModule } from '@ngx-translate/core';
import { AutocompleteComponent } from '../../../../../base/shared/autocomplete/autocomplete.component';
import { ClientAutocompleteComponent } from '../../../../../base/shared/client-autocomplete/client-autocomplete.component';

@Component({
    selector: 'app-false-recording-form',
    templateUrl: './false-recording-form.component.html',
    styleUrls: ['./false-recording-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        ClientAutocompleteComponent,
        DatePickerModule,
        AutocompleteComponent,
        TranslateModule
    ]
})
export class FalseRecordingFormComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ClientAutocompleteComponent)
  public clientAutocomplete: ClientAutocompleteComponent;

  @ViewChild('startTime')
  public startTimePicker: DatePickerComponent;

  @ViewChild('endTime')
  public endTimePicker: DatePickerComponent;

  @Input() public form: UntypedFormGroup;

  @Output()
  public searchDTOChanged = new EventEmitter<any>();

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
    }
    // Defer the event emission to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.onClientSelected(value);
    });
  }

  public get selectedZone(): number {
    if (this.form.get('zoneId').value === null) {
      return null;
    }
    return this.form.get('zoneId').value;
  }

  public set selectedZone(value: number) {
    //TODO Currently workaround
    //Check if actual record has been selected
    if (typeof value === 'number') {
      this.form.get('zoneId').setValue(value);
    } else {
      this.form.get('zoneId').setValue(null);
    }
  }

  public get selectedAgent(): number {
    if (this.form.get('agent').value === null) {
      return null;
    }
    return this.form.get('agent').value;
  }

  public set selectedAgent(value: number) {
    //TODO Currently workaround
    //Check if actual record has been selected
    if (typeof value === 'string') {
      this.form.get('agent').setValue(value);
    } else {
      this.form.get('agent').setValue(null);
    }
  }

  public subscriptions = [];

  constructor(protected clientService: ClientService,
              private falseRecordingReportService: FalseRecordingReportService) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        if (this.endTimePicker) {
          if (this.form.get('startDate').value) {
            this.endTimePicker.min = this.form.get('startDate').value;
          } else {
            this.endTimePicker.min = null;
          }
        }

        if (this.startTimePicker) {
          if (this.form.get('endDate').value) {
            this.startTimePicker.max = this.form.get('endDate').value;
          } else {
            this.startTimePicker.max = null;
          }
        }
        this.searchDTOChanged.emit();
      }));
  }

  ngAfterViewInit(): void {
    if (!this.selectedClient) {
      this.focusClientAutocomplete();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public onClientSelected(client: ClientDTO): void {
    if (client) {
      this.searchDTOChanged.emit();
    }
  }

  public searchZoneAutocompleteValues(searchText: string): Observable<IAutocompleteItem[]> {
    return this.falseRecordingReportService.searchZones(searchText)
      .pipe(map((zoneAutocompleteValues: PagedResultDTOOfZoneDTO) => {
        const data: IAutocompleteItem[] = [];
        zoneAutocompleteValues.items.forEach(zone => {
          data.push({id: zone.id, name: zone.name});
        });
        return data;
      }));
  }

  public searchAgentAutocompleteValues(searchText: string): Observable<IAutocompleteItem[]> {
    return this.falseRecordingReportService.searchAgents(searchText)
      .pipe(map((agentAutocompleteValues: PagedResultDTOOfUserInfoDTO) => {
        const data: IAutocompleteItem[] = [];
        agentAutocompleteValues.items.forEach(user => {
          data.push({id: user.userId, name: `${user.firstname} ${user.lastname}`});
        });
        return data;
      }));
  }

  private focusClientAutocomplete(): void {
    if (this.clientAutocomplete) {
      this.clientAutocomplete.focus();
    }
  }
}
